import React, { useState, useEffect } from 'react';
import { Card, Alert, Button, InputNumber, Form, message, notification, Modal } from 'antd';
import { ExclamationCircleOutlined, EditOutlined, DollarOutlined } from '@ant-design/icons';
import { PointOfSaleTransaction } from '../../../model/pointofsale/PointOfSaleTransaction';
import { PointOfSaleTransactionItem } from '../../../model/pointofsale/PointOfSaleTransactionItem';
import { UpdatePaymentTransactionRequest } from '../../../model/pointofsale/UpdatePaymentTransactionRequest';
import { usePointOfSaleService } from '../../../services/pointofsale/PointOfSaleServiceProvider';
import { PointOfSaleItem } from '../../../model/pointofsale/PointOfSaleItem';
import { useFormatCurrency, useCurrencySymbol } from '../../../hooks/useFormatters';

interface JoiningFeeManagerProps {
  membershipId: string;
  pointOfSaleTransactions: PointOfSaleTransaction[];
  backgroundColor?: string;
  borderColor?: string;
  onTransactionUpdated?: () => void;
}

const JoiningFeeManager: React.FC<JoiningFeeManagerProps> = ({
  membershipId,
  pointOfSaleTransactions,
  backgroundColor = '#ffffff',
  borderColor = '#e5e7eb',
  onTransactionUpdated
}) => {
  const [form] = Form.useForm();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [memberJoiningFeePosItem, setMemberJoiningFeePosItem] = useState<PointOfSaleItem | null>(null);
  const pointOfSaleService = usePointOfSaleService();
  const formatCurrency = useFormatCurrency();
  const currencySymbol = useCurrencySymbol();

  useEffect(() => {
    const fetchMemberJoiningFeePosItem = async () => {
      try {
        const item = await pointOfSaleService.getPosItem("Initial Member Joining Fee");
        setMemberJoiningFeePosItem(item);
      } catch (error) {
        console.error('Error fetching member joining fee POS item:', error);
      }
    };
    fetchMemberJoiningFeePosItem();
  }, [pointOfSaleService]);

  // Find any incomplete joining fee transactions
  const findIncompleteJoiningFeeTransaction = (): PointOfSaleTransaction | null => {
    return pointOfSaleTransactions.find(transaction => 
      transaction.status !== 'Completed' &&
      transaction.items.some(item => 
        item.item.name === "Initial Member Joining Fee"
      )
    ) || null;
  };

  const incompleteTransaction = findIncompleteJoiningFeeTransaction();

  const getJoiningFeeItem = (transaction: PointOfSaleTransaction): PointOfSaleTransactionItem | null => {
    return transaction.items.find(item => 
      item.item.name === "Initial Member Joining Fee"
    ) || null;
  };

  const handleUpdatePrice = async (values: { newPrice: number }) => {
    if (!incompleteTransaction || !memberJoiningFeePosItem) {
      message.error('Unable to update price: transaction or joining fee item not found');
      return;
    }

    setUpdating(true);
    try {
      // Create update request for existing transaction
      const updateRequest: UpdatePaymentTransactionRequest = {
        transactionId: incompleteTransaction.transactionId,
        status: incompleteTransaction.status,
        amount: values.newPrice,
        items: incompleteTransaction.items,
        bookingSlots: incompleteTransaction.bookingSlots
      };

      await pointOfSaleService.updateTransaction(updateRequest);
      
      message.success('Joining fee price updated successfully!');
      setIsEditModalVisible(false);
      form.resetFields();
      
      if (onTransactionUpdated) {
        onTransactionUpdated();
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to update joining fee price'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!incompleteTransaction) {
      message.error('No incomplete joining fee transaction found');
      return;
    }

    setUpdating(true);
    try {
      const updateRequest: UpdatePaymentTransactionRequest = {
        transactionId: incompleteTransaction.transactionId,
        status: 'Completed',
        amount: incompleteTransaction.amount,
        items: incompleteTransaction.items,
        bookingSlots: incompleteTransaction.bookingSlots
      };

      await pointOfSaleService.updateTransaction(updateRequest);
      
      message.success('Joining fee marked as paid successfully!');
      
      if (onTransactionUpdated) {
        onTransactionUpdated();
      }
    } catch (error) {
      console.error('Error marking transaction as paid:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to mark joining fee as paid'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkAsUnpaid = async () => {
    if (!incompleteTransaction) {
      message.error('No incomplete joining fee transaction found');
      return;
    }

    setUpdating(true);
    try {
      const updateRequest: UpdatePaymentTransactionRequest = {
        transactionId: incompleteTransaction.transactionId,
        status: 'Unpaid',
        amount: incompleteTransaction.amount,
        items: incompleteTransaction.items,
        bookingSlots: incompleteTransaction.bookingSlots
      };

      await pointOfSaleService.updateTransaction(updateRequest);
      
      message.success('Joining fee marked as unpaid successfully!');
      
      if (onTransactionUpdated) {
        onTransactionUpdated();
      }
    } catch (error) {
      console.error('Error marking transaction as unpaid:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to mark joining fee as unpaid'
      });
    } finally {
      setUpdating(false);
    }
  };

  const showEditModal = () => {
    if (incompleteTransaction) {
      const joiningFeeItem = getJoiningFeeItem(incompleteTransaction);
      if (joiningFeeItem) {
        form.setFieldsValue({ newPrice: joiningFeeItem.item.price });
      }
    }
    setIsEditModalVisible(true);
  };

  if (!incompleteTransaction) {
    return null; // Don't render if no incomplete joining fee transaction
  }

  const joiningFeeItem = getJoiningFeeItem(incompleteTransaction);
  if (!joiningFeeItem) {
    return null;
  }

  return (
    <>
      <Card
        title="⚠️ Incomplete Joining Fee"
        className="mb-4"
        style={{ 
          backgroundColor: '#fff2f0',
          borderColor: '#ffccc7'
        }}
      >
        <Alert
          message="Incomplete Initial Member Joining Fee"
          description={
            <div>
              <p>This member has an incomplete joining fee transaction.</p>
              <p><strong>Current Amount:</strong> {formatCurrency(joiningFeeItem.item.price)}</p>
              <p><strong>Status:</strong> {incompleteTransaction.status}</p>
              <p><strong>Transaction ID:</strong> {incompleteTransaction.transactionId}</p>
            </div>
          }
          type="warning"
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
        
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={showEditModal}
            loading={updating}
          >
            Update Price
          </Button>
          
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={handleMarkAsPaid}
            loading={updating}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            Mark as Paid
          </Button>
          
          <Button
            onClick={handleMarkAsUnpaid}
            loading={updating}
          >
            Mark as Unpaid
          </Button>
        </div>
      </Card>

      <Modal
        title="Update Joining Fee Price"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdatePrice}
        >
          <Form.Item
            label={`New Price (${currencySymbol})`}
            name="newPrice"
            rules={[
              { required: true, message: 'Please enter a price' },
              { type: 'number', min: 0, message: 'Price must be positive' }
            ]}
          >
            <InputNumber
              inputMode="decimal"
              style={{ width: '100%' }}
              placeholder="Enter new price"
              precision={2}
              min={0}
              step={0.01}
              prefix={currencySymbol}
            />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button 
              onClick={() => {
                setIsEditModalVisible(false);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={updating}
            >
              Update Price
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default JoiningFeeManager;